import type {
  ContactRequest,
  SelectOption,
  SiteRequest,
  SiteResponse,
} from 'src/api/ProactApiClient';
import {
  COUNTRY_OPTIONS,
  TIMEZONE_OPTIONS,
} from 'src/shared/constants/geoOptions';
import type {ISelectOption} from 'src/types/Form';
import type {IFormFieldV2} from 'src/types/FormV2';

export {COUNTRY_OPTIONS, TIMEZONE_OPTIONS};

export const SiteDocumentTitle = 'Sites';

const SiteNameFieldsMeta: IFormFieldV2[] = [
  {
    name: 'name',
    label: 'Name',
    type: 'text',
    required: true,
    validation: {maxLength: 255},
    size: 'md',
  },
  {
    name: 'siteNumber',
    label: 'Site Number',
    type: 'text',
    required: false,
    validation: {maxLength: 100},
    size: 'md',
  },
];

// Shared between the edit form and the read-only view — client association
// is intentionally excluded here since the two modes render it differently
// (an editable select vs. a plain clientName link).
const SiteAddressFieldsMeta: IFormFieldV2[] = [
  {
    label: 'Address',
    name: 'heading1',
    type: 'heading',
  },
  {
    name: 'address',
    label: 'Address',
    type: 'text',
    required: false,
    validation: {maxLength: 255},
    size: 'md',
  },
  {
    name: 'city',
    label: 'City',
    type: 'text',
    required: false,
    validation: {maxLength: 100},
    size: 'md',
  },
  {
    name: 'state',
    label: 'State',
    type: 'text',
    required: false,
    validation: {maxLength: 100},
    size: 'xs',
  },
  {
    name: 'country',
    label: 'Country',
    type: 'select',
    options: COUNTRY_OPTIONS,
    required: false,
    size: 'xs',
  },
  {
    name: 'postalCode',
    label: 'Postal Code',
    type: 'text',
    required: false,
    validation: {maxLength: 20},
    size: 'xs',
  },
  {
    name: 'timeZone',
    label: 'Time Zone',
    type: 'select',
    options: TIMEZONE_OPTIONS,
    required: true,
    size: 'xs',
  },
];

export const SiteFormMeta = (clients: SelectOption[]): IFormFieldV2[] => [
  ...SiteNameFieldsMeta,
  {
    name: 'clientId',
    label: 'Client',
    type: 'select',
    options: clients as ISelectOption[],
    required: false,
    description:
      'The client company this site belongs to. Leave blank for an unassociated site.',
  },
  ...SiteAddressFieldsMeta,
];

// Read-only general-info display. Client association renders separately
// (as a plain clientName link) so the view doesn't depend on the client
// select-options query.
export const SiteViewGeneralMeta: IFormFieldV2[] = [
  ...SiteNameFieldsMeta,
  ...SiteAddressFieldsMeta,
];

// Latitude/longitude are populated later by geocoding — display-only, never
// part of the editable form meta.
export const SiteCoordinatesViewMeta: IFormFieldV2[] = [
  {
    name: 'latitude',
    label: 'Latitude',
    type: 'number',
    required: false,
  },
  {
    name: 'longitude',
    label: 'Longitude',
    type: 'number',
    required: false,
  },
];

// Newtonsoft sends explicit `null` for absent nested values where the TS
// types say `undefined` — normalize at this response -> request boundary.
export const MapSiteResponseToSiteRequest = (
  data: SiteResponse
): SiteRequest => {
  return {
    id: data.id,
    // The 'select' field type stores the chosen option id as a string;
    // coerce back to number | undefined for the nullable clientId.
    clientId: data.clientId ? Number(data.clientId) : undefined,
    name: data.name,
    siteNumber: data.siteNumber ?? undefined,
    address: data.address ?? undefined,
    city: data.city ?? undefined,
    state: data.state ?? undefined,
    country: data.country ?? undefined,
    postalCode: data.postalCode ?? undefined,
    timeZone: data.timeZone ?? undefined,
    contacts: (data.contacts ?? []).map(
      (contact): ContactRequest => ({
        id: contact.id,
        name: contact.name ?? undefined,
        title: contact.title ?? undefined,
        email: contact.email ?? undefined,
        phone: contact.phone ?? undefined,
      })
    ),
  };
};
